import React, { useEffect, useState, useCallback } from 'react';
import { Wallet, ArrowUpRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { getPayoutData, requestPayout } from '@/lib/api';

const PayoutsPage = () => {
  const { dealer } = useAuth();
  const [balance, setBalance] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPayoutData = useCallback(async () => {
    if (!dealer) return;
    try {
      const res = await getPayoutData(dealer.id);
      setBalance(res.data.balance);
      setPayouts(res.data.payouts);
    } catch (error) {
      console.error('Error fetching payout data:', error);
    }
  }, [dealer]);

  useEffect(() => {
    if (dealer) {
      fetchPayoutData();
    }
  }, [dealer, fetchPayoutData]);

  const handleRequestPayout = async () => {
    if (balance < 500) {
      toast.error('Minimum payout amount is ₹500');
      return;
    }

    setLoading(true);
    try {
      await requestPayout(dealer.id, balance);
      toast.success('Payout request submitted successfully!');
      fetchPayoutData();
    } catch (error) {
      console.error('Error requesting payout:', error);
      toast.error(error.message || 'Failed to request payout');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      requested: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      processed: 'bg-green-100 text-green-800 border-green-200'
    };

    return (
      <Badge variant="outline" className={variants[status] || ''}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="payouts-title">Payouts</h1>
          <p className="text-slate-600 mt-1">Manage your earnings and withdrawals</p>
        </div>

        {/* Balance Card */}
        <Card className="border-2 border-slate-200 shadow-sm" data-testid="balance-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Available Balance
            </CardTitle>
            <CardDescription>Your UPI ID: {dealer?.upi_id}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-4xl font-bold text-green-600" data-testid="available-balance">
              ₹{balance.toLocaleString()}
            </div>
            <Button
              onClick={handleRequestPayout}
              data-testid="request-payout-button"
              disabled={loading || balance < 500}
              className="w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowUpRight className="mr-2 h-4 w-4" />
                  Request Payout
                </>
              )}
            </Button>
            {balance < 500 && (
              <p className="text-xs text-slate-500">Minimum payout amount is ₹500</p>
            )}
          </CardContent>
        </Card>

        {/* Payout History */}
        <Card data-testid="payout-history-card">
          <CardHeader>
            <CardTitle>Payout History</CardTitle>
            <CardDescription>Track all your withdrawal requests</CardDescription>
          </CardHeader>
          <CardContent>
            {payouts.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="mx-auto h-12 w-12 text-slate-300" />
                <h3 className="mt-4 text-lg font-medium text-slate-900">No payouts yet</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Request a payout once you have earned commission
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Date</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Amount</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-slate-900">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid="payout-row">
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {new Date(payout.created_at).toLocaleDateString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-semibold text-slate-900">
                          ₹{payout.amount?.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-center">{getStatusBadge(payout.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PayoutsPage;
